import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const HomePage = () => {
  const navigate = useNavigate();

  const handleQuestionClick = (path: string) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-emerald-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: '"Pacifico", serif' }}>
            Life 마니또
          </div>
          <div className="text-sm text-gray-600">
            CF 생리학 연구
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Title Section */}
        <div className="text-center mb-16 animate-fade-in">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            낭포성 섬유증(CF): 무엇이, 왜 문제일까요?
          </h1>
          <p className="text-2xl text-gray-600 mb-8">
            일상을 지켜줘! : Life 마니또
          </p>
        </div>

        {/* Problem Case Presentation */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-8 mb-12 rounded-lg shadow-sm">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">문제 상황 제시</h2>
          <p className="text-gray-700 leading-relaxed text-lg">
            10세 남아 홍길동 군은 유전 질환인 낭포성 섬유증(Cystic Fibrosis)을 앓고 있습니다. 이 질환으로 인해 폐와 췌장 등 여러 기관에서 
            점액이 비정상적으로 끈끈하게 분비됩니다. 홍 군은 잦은 기침과 호흡곤란으로 체육 활동에 참여하기 어렵고, 췌장 기능 부전으로 
            소화효소가 부족하여 영양 흡수에 어려움을 겪고 있습니다. 이로 인해 또래보다 체구가 작고, 매 식사 시 소화효소제를 복용해야 하는 
            등 복잡한 건강 관리로 인해 학교생활과 교우 관계 형성에 어려움을 느끼고 있습니다.
          </p>
        </div>

        {/* Team Name Explanation */}
        <div className="bg-green-50 border-l-4 border-green-500 p-8 mb-12 rounded-lg shadow-sm">
          <h2 className="text-2xl font-bold text-green-800 mb-4">팀 이름의 이유</h2>
          <p className="text-gray-700 leading-relaxed text-lg">
            학교에 같이 가서 직접적 도움을 주지는 않지만 늘 곁에서 생각하고 일상을 잘 살아갈수있도록 적응하길 바라는 마음으로 라이프 마니또라는 팀명을 정하게 되었습니다.
          </p>
        </div>

        {/* Introduction Text */}
        <div className="text-center mb-16">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
            <p className="text-xl text-gray-700 leading-relaxed">
              <strong>낭포성 섬유증</strong>은 <strong>CFTR 유전자 변이</strong>로 인해 발생하는 <strong>상염색체 열성 유전 질환</strong>입니다. 이 유전자 변이는 우리 몸의 <strong>상피세포 기능</strong>에 문제를 일으켜, 
              주로 <strong>폐와 췌장</strong> 등 여러 장기에 영향을 미칩니다. 이 질환이 아동의 신체에 어떻게 영향을 미치는지 <strong>4가지 핵심 질문</strong>을 통해 알아보겠습니다. 
              아래 버튼을 클릭하여 각 기전을 순서대로 확인해 보세요.
            </p>
          </div>
        </div>

        {/* Question Buttons */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <button
            onClick={() => handleQuestionClick('/q1')}
            className="group bg-white hover:bg-blue-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-left border-2 border-transparent hover:border-blue-200"
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors">
                <span className="text-blue-600 font-bold text-xl">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">점액은 왜 끈끈해질까요?</h3>
            </div>
            <p className="text-gray-600 text-lg">CFTR 채널의 기능 장애와 점액 점도 증가 메커니즘을 알아봅니다.</p>
          </button>

          <button
            onClick={() => handleQuestionClick('/q2')}
            className="group bg-white hover:bg-red-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-left border-2 border-transparent hover:border-red-200"
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 group-hover:bg-red-200 transition-colors">
                <span className="text-red-600 font-bold text-xl">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">왜 폐 감염이 반복될까요?</h3>
            </div>
            <p className="text-gray-600 text-lg">끈끈한 점액이 폐의 방어 기능에 미치는 영향을 살펴봅니다.</p>
          </button>

          <button
            onClick={() => handleQuestionClick('/q3')}
            className="group bg-white hover:bg-orange-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-left border-2 border-transparent hover:border-orange-200"
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4 group-hover:bg-orange-200 transition-colors">
                <span className="text-orange-600 font-bold text-xl">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">소화는 왜 어려울까요?</h3>
            </div>
            <p className="text-gray-600 text-lg">췌장관 폐쇄가 영양소 흡수에 미치는 영향을 확인합니다.</p>
          </button>

          <button
            onClick={() => handleQuestionClick('/q4')}
            className="group bg-white hover:bg-purple-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-left border-2 border-transparent hover:border-purple-200"
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4 group-hover:bg-purple-200 transition-colors">
                <span className="text-purple-600 font-bold text-xl">4</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">성장에 미치는 영향은?</h3>
            </div>
            <p className="text-gray-600 text-lg">복합적인 요인들이 아동의 성장과 학습에 미치는 영향을 분석합니다.</p>
          </button>
        </div>

        {/* Navigation to Conclusion */}
        <div className="text-center">
          <button
            onClick={() => handleQuestionClick('/conclusion')}
            className="bg-green-600 hover:bg-green-700 text-white px-12 py-4 rounded-xl font-bold text-xl transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            결론 보기
          </button>
        </div>
      </main>
    </div>
  );
};

export default HomePage;
